"use client";

import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { telemetry } from "@/lib/telemetry";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Code2, Sparkles, RefreshCw, Zap } from "lucide-react";
import { getViewModeChrome } from "../view-mode-chrome";
import { API_BASE_URL } from "@/lib/config";
import { FundAgentProgressCard } from "@/components/notebook/FundAgentProgressCard";

const getStages = (
  type: "add" | "replace" | "edit" = "add"
) => {
    if (type === "edit") {
      return [
        {
          id: 1,
          text: "Refining analysis...",
          duration: 500,
          icon: RefreshCw,
          iconColor: "text-muted-foreground",
          bgColor: "bg-muted/50",
        },
        {
          id: 2,
          text: "Applying updates...",
          duration: 100,
          icon: Zap,
          iconColor: "text-muted-foreground",
          bgColor: "bg-muted/60",
        },
        {
          id: 3,
          text: "Re-running analysis...",
          duration: 1000,
          icon: Sparkles,
          iconColor: "text-foreground",
          bgColor: "bg-muted/70",
        },
      ];
    }
    if (type === "replace") {
      return [
        {
          id: 1,
          text: "Identifying issues...",
          duration: 500,
          icon: Search,
          iconColor: "text-muted-foreground",
          bgColor: "bg-muted/50",
        },
        {
          id: 2,
          text: "Rewriting analysis...",
          duration: 100,
          icon: RefreshCw,
          iconColor: "text-muted-foreground",
          bgColor: "bg-muted/60",
        },
        {
          id: 3,
          text: "Applying changes...",
          duration: 1000,
          icon: Zap,
          iconColor: "text-foreground",
          bgColor: "bg-muted/70",
        },
      ];
    }
    return [
      {
        id: 1,
        text: "Analyzing market data...",
        duration: 500,
        icon: Search,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/50",
      },
      {
        id: 2,
        text: "Building analysis...",
        duration: 1000,
        icon: Code2,
        iconColor: "text-muted-foreground",
        bgColor: "bg-muted/60",
      },
      {
        id: 3,
        text: "Executing analysis...",
        duration: 1000,
        icon: Sparkles,
        iconColor: "text-foreground",
        bgColor: "bg-muted/70",
      },
    ];
  }

interface AddCellLoadingProps {
  type?: "add" | "replace" | "edit";
  /** When true (view_mode === "alert"), copy becomes "Adding step to alert". */
  alertMode?: boolean;
  viewMode?: string;
  /**
   * Session id — when provided (live chats only), the card polls
   * sub-notebook progress while the cell executes so SEC/fund/sub-agent
   * work shows inline instead of looking like a hung "Analyzing..." card.
   */
  sessionId?: string;
}

/**
 * Polls /strategies/sub-notebook-progress/{sessionId} only while this
 * loading card is mounted (i.e. while a cell is actually executing), so
 * cells without sub-agents cost a few no-op requests at most. Returns the
 * sub_notebooks map of the most recent cell that has sub-agents.
 */
function useInlineSubProgress(sessionId?: string) {
  // One-shot telemetry flags per card mount so polling every 2.5s doesn't
  // spam events.
  const emittedShownRef = useRef(false);
  const emittedStaleRef = useRef(false);
  const emittedErrorRef = useRef(false);

  const { data } = useSWR(
    sessionId
      ? `${API_BASE_URL}/strategies/sub-notebook-progress/${sessionId}`
      : null,
    (url: string) =>
      fetch(url).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      }),
    {
      refreshInterval: 2500,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: true,
      onError: (err) => {
        if (emittedErrorRef.current) return;
        emittedErrorRef.current = true;
        telemetry.emit("inline_progress.fetch_error", {
          session_id: sessionId,
          error: String(err?.message || err),
        });
      },
    },
  );

  let result: Record<string, any> | null = null;
  let staleFiltered = 0;
  try {
    const cells = data?.cells_with_sub_agents;
    if (Array.isArray(cells) && cells.length > 0) {
      const latest = cells[cells.length - 1];
      const subNotebooks = latest?.sub_notebooks as
        | Record<string, any>
        | undefined;
      if (subNotebooks && typeof subNotebooks === "object") {
        // "not_found" = the sub-notebook was already deleted by
        // _kill_sub_kernels after the agents finished (finished runs report
        // this forever) — filter those so a dead "Query 1" row never
        // resurrects on later cells. Anything else is genuinely alive RIGHT
        // NOW. Do NOT require status === "executing": fund agents spend most
        // of their runtime in LLM turns where the notebook sits "idle"
        // between cell executions, and an executing-only gate hides the tree
        // for almost the entire run (verified via agent_events 2026-07-02).
        const alive = Object.fromEntries(
          Object.entries(subNotebooks).filter(([, nb]: [string, any]) => {
            const keep = Boolean(nb) && nb.status !== "not_found";
            if (!keep) staleFiltered += 1;
            return keep;
          }),
        );
        if (Object.keys(alive).length > 0) {
          result = alive as Record<string, any>;
        }
      }
    }
  } catch {
    // Malformed payload — never let the loading card crash the chat.
    result = null;
  }

  useEffect(() => {
    if (result && !emittedShownRef.current) {
      emittedShownRef.current = true;
      telemetry.emit("inline_progress.shown", {
        session_id: sessionId,
        sub_notebook_count: Object.keys(result).length,
      });
    }
    if (!result && staleFiltered > 0 && !emittedStaleRef.current) {
      emittedStaleRef.current = true;
      telemetry.emit("inline_progress.stale_filtered", {
        session_id: sessionId,
        filtered_count: staleFiltered,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result === null, staleFiltered, sessionId]);

  return result;
}

export function AddCellLoading({
  type = "add",
  alertMode = false,
  viewMode,
  sessionId,
}: AddCellLoadingProps) {
  const chrome = getViewModeChrome(viewMode);
  const [currentStage, setCurrentStage] = useState(0);
  const subNotebooks = useInlineSubProgress(sessionId);
  const alertStageIcon = chrome.isSignal ? "text-emerald-100/90" : "text-sky-100/90";
  const alertStageBg = chrome.isSignal ? "bg-emerald-300/[0.08]" : "bg-sky-300/[0.08]";
  const stages = alertMode && type === "add"
    ? [
        { id: 1, text: "Adding step to alert…", duration: 700, icon: Sparkles, iconColor: alertStageIcon, bgColor: alertStageBg },
        { id: 2, text: "Running step…", duration: 1000, icon: Code2, iconColor: alertStageIcon, bgColor: alertStageBg },
      ]
    : getStages(type);

  useEffect(() => {
    setCurrentStage(0);
    const timers: NodeJS.Timeout[] = [];
    let totalDelay = 0;

    stages.forEach((stage, index) => {
      const timer = setTimeout(() => {
        setCurrentStage(index);
      }, totalDelay);

      totalDelay += stage.duration;
      timers.push(timer);
    });

    return () => timers.forEach(clearTimeout);
  }, [type, stages]);

  const currentStageData = stages[currentStage] || stages[0];
  const Icon = currentStageData.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        {/* Progress bar */}
        <div className="absolute top-0 left-0 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent">
          <motion.div
            className="h-full bg-gradient-to-r from-foreground/0 via-foreground/80 to-foreground/0"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 3, ease: "linear" }}
          />
        </div>

        <div className="px-3 py-2 flex items-center gap-3 min-w-0">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStage}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2.5"
            >
              {/* Animated icon with stage-specific styling */}
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className={`w-7 h-7 rounded ${currentStageData.bgColor} flex items-center justify-center`}
              >
                <Icon
                  className={`w-4 h-4 ${alertMode && type === "add" ? currentStageData.iconColor : chrome.iconMuted}`}
                  strokeWidth={2}
                />
              </motion.div>

              {/* Stage text */}
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {subNotebooks ? "Running research agents..." : currentStageData.text}
              </span>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Inline sub-agent (SEC/fund/research) progress — live chats only */}
        {subNotebooks && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="px-3 pb-2 border-t border-border/50 pt-2"
          >
            <FundAgentProgressCard subNotebooks={subNotebooks} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
